/**
 * Cards lent to the Assistant by other parts of the app.
 *
 * The browser half of the backend's extension seam (`assistant/extensions.py`):
 * a tool the Assistant was lent may end its turn with a `{kind, payload}` card,
 * and whoever lent the tool registers how that kind is drawn. The chat knows
 * that cards exist, never which ones - a kind nobody registered (the part of
 * the app that drew it is switched off, or the chat is old) draws nothing
 * rather than guessing.
 *
 * A kind may also act once when its answer arrives (`arrive`): open a place,
 * or put a change into an open editor, unsaved. It runs for a fresh answer
 * only, never when an old chat is read back.
 */

import { nothing, type TemplateResult } from 'lit';
import type { AssistantCard } from '../../api/types.ts';

export type AssistantCardRenderer = (payload: Record<string, unknown>) => TemplateResult;
export type AssistantCardArrival = (payload: Record<string, unknown>, host: EventTarget) => void;

const renderers = new Map<string, AssistantCardRenderer>();
const arrivals = new Map<string, AssistantCardArrival>();

export function registerAssistantCard(
  kind: string,
  render: AssistantCardRenderer,
  arrive?: AssistantCardArrival,
): void {
  renderers.set(kind, render);
  if (arrive) arrivals.set(kind, arrive);
}

export function renderAssistantCard(card: AssistantCard): TemplateResult | typeof nothing {
  const render = renderers.get(card.kind);
  return render ? render(card.payload ?? {}) : nothing;
}

/** Run a fresh card's arrival, if its kind has one. */
export function arriveAssistantCard(card: AssistantCard, host: EventTarget): void {
  try {
    arrivals.get(card.kind)?.(card.payload ?? {}, host);
  } catch {
    // The card is still drawn; it says what it holds.
  }
}
